// @ts-nocheck — React class components require `this.state`/`this.props`
// which TS can't resolve under `types: ["node"]` in tsconfig.
// Error boundaries cannot be functional components (React limitation).
import React from 'react';
import { ErrorOverlay } from './ErrorOverlay';

interface CaptureErrorBoundaryProps {
  taskName?: string;
  onClose: () => void;
  children: React.ReactNode;
}

interface CaptureErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React error boundary that wraps capture flows.
 * Catches render/lifecycle errors and shows ErrorOverlay
 * instead of a blank screen.
 */
export class CaptureErrorBoundary extends React.Component<
  CaptureErrorBoundaryProps,
  CaptureErrorBoundaryState
> {
  constructor(props: CaptureErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error: Error): CaptureErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[CaptureErrorBoundary]', error, info.componentStack);
  }

  handleRetry(): void {
    this.setState({ hasError: false, error: null });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      const icon = (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" x2="12" y1="8" y2="12" />
          <line x1="12" x2="12.01" y1="16" y2="16" />
        </svg>
      );

      return (
        <ErrorOverlay
          taskName={this.props.taskName}
          icon={icon}
          title="Something went wrong"
          description={this.state.error?.message || 'An unexpected error occurred during capture.'}
          onRetry={this.handleRetry}
          onClose={this.props.onClose}
        />
      );
    }

    return this.props.children;
  }
}
